import { useState } from 'react';
import { formatDateToIso, plcDayjs } from '@utils/date';

export enum MobileOSInstallPromptEnum {
  ANDROID = 'androidInstallPromptedAt',
  IOS = 'iosInstallPromptedAt',
}

/**
 * Function that retrieved based on the given parameter the value stored inside his localStorage.
 * @param promptName
 * @returns The defined value inside the user local storage browser
 */
const getInstallPromptLastSeenAt = (promptName: string): string | null => localStorage.getItem(promptName);

/**
 * Function to store the given parameter inside the user's localStorage
 * @param promptName
 */
const setInstallPromptSeenToday = (promptName: string): void => {
  const today = formatDateToIso(new Date());
  localStorage.setItem(promptName, today);
};

/**
 * Determiner wheter we should prompted the pwa installation banner to the user.
 * @param promptName
 * @param daysToWaitBeforePromptingAgain
 * @returns true or false
 */
function getUserShouldBePromptedToInstall(promptName: string, daysToWaitBeforePromptingAgain: number): boolean {
  const localStorageVal = getInstallPromptLastSeenAt(promptName);
  if (localStorageVal === null) {
    return true;
  }
  const lastPrompt = plcDayjs(getInstallPromptLastSeenAt(promptName));
  const daysSinceLastPrompt = plcDayjs().diff(lastPrompt, 'days');
  return Number.isNaN(daysSinceLastPrompt) || daysSinceLastPrompt > daysToWaitBeforePromptingAgain;
}

/**
 * Custom hooks that handle the pwa banner installation.
 * @param promptName
 * @param daysToWaitBeforePromptingAgain
 * @returns Array containing the boolean to know wheter to ask and the handler to display or not the install banner with time management.
 */
const useShouldShowPrompt = (promptName: string, daysToWaitBeforePromptingAgain = 30): [boolean, () => void] => {
  const [userShouldBePromptedToInstall, setUserShouldBePromptedToInstall] = useState(
    getUserShouldBePromptedToInstall(promptName, daysToWaitBeforePromptingAgain)
  );

  const handleUserSeeingInstallPrompt = () => {
    setUserShouldBePromptedToInstall(false);
    setInstallPromptSeenToday(promptName);
  };

  return [userShouldBePromptedToInstall, handleUserSeeingInstallPrompt];
};
export default useShouldShowPrompt;
